import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

const STATUS = {
  active:   { dot: 'var(--green)',  label: 'Active' },
  on_break: { dot: 'var(--amber)',  label: 'On Break' },
  leave:    { dot: 'var(--violet)', label: 'Leave' },
  offline:  { dot: 'var(--text-3)', label: 'Offline' },
};

const NAV = (role) => [
  { to: '/',              label: 'Dashboard',    exact: true },
  { to: '/calling',       label: 'Calling Desk' },
  { to: '/pipeline',      label: 'Pipeline' },
  { to: '/tasks',         label: 'Tasks' },
  { to: '/demos',         label: 'Demos' },
  { to: '/attendance',    label: 'Attendance' },
  { to: '/followups',     label: 'Follow-ups' },
  { to: '/leaves',        label: 'Leave' },
  { to: '/wallet',        label: 'Wallet' },
  { to: '/issues',        label: 'Issues' },
  ...(role === 'superadmin' ? [
    { divider: true },
    { to: '/users',         label: 'Users' },
    { to: '/calling-data',  label: 'Calling Data' },
    { to: '/reports',       label: 'Reports' },
    { to: '/announcements', label: 'Announcements' },
  ] : [])
];

export default function Layout() {
  const { user, logout, updateStatus } = useAuth();
  const navigate = useNavigate();
  const [showStatus, setShowStatus] = useState(false);
  const [showNotifs, setShowNotifs] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const statusRef = useRef(null);
  const notifRef = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) setShowStatus(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifs(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get('/notifications/unread-count');
      setUnreadCount(data.count);
    } catch (e) {}
  }, [user]);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.slice(0, 10));
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  const openNotifs = () => {
    setShowNotifs(v => !v);
    if (!showNotifs) fetchNotifications();
  };

  const markRead = async (n) => {
    if (!n.read) {
      try {
        await api.put(`/notifications/${n._id}/read`);
        setNotifications(prev => prev.map(x => x._id === n._id ? { ...x, read: true } : x));
        setUnreadCount(c => Math.max(0, c - 1));
      } catch (e) {}
    }
    if (n.link) { navigate(n.link); setShowNotifs(false); }
  };

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(x => ({ ...x, read: true })));
      setUnreadCount(0);
    } catch (e) {}
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    toast.success('Signed out');
  };

  const handleStatus = async (s) => {
    await updateStatus(s);
    setShowStatus(false);
    toast.success(`Status: ${STATUS[s].label}`);
  };

  const sc = STATUS[user?.status] || STATUS.offline;
  const items = NAV(user?.role);

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--bg)' }}>
      {/* Sidebar */}
      <aside style={{
        width: 220, background: 'var(--surface)', borderRight: '1px solid var(--border)',
        display: 'flex', flexDirection: 'column', position: 'fixed', height: '100vh', zIndex: 50
      }}>
        {/* Logo */}
        <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 26, height: 26, background: 'var(--accent)', borderRadius: 'var(--r-sm)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0
            }}>X</div>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text-1)', letterSpacing: '-0.2px' }}>Xion Console</span>
          </div>
        </div>

        {/* Notification bell */}
        <div ref={notifRef} style={{ position: 'relative', padding: '8px 8px 0' }}>
          <button
            onClick={openNotifs}
            style={{
              width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-sm)', padding: '6px 10px', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 7, fontSize: 13,
              color: 'var(--text-2)', fontFamily: 'inherit', position: 'relative'
            }}
          >
            <span style={{ fontSize: 16 }}></span>
            <span style={{ flex: 1, textAlign: 'left' }}>Notifications</span>
            {unreadCount > 0 && (
              <span style={{
                background: 'var(--red)', color: '#fff', borderRadius: 99,
                padding: '1px 6px', fontSize: 11, fontWeight: 700, minWidth: 18, textAlign: 'center'
              }}>{unreadCount}</span>
            )}
          </button>

          {showNotifs && (
            <div style={{
              position: 'absolute', top: '110%', left: 8, right: 8,
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-md)',
              zIndex: 300, maxHeight: 360, overflowY: 'auto'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 12px', borderBottom: '1px solid var(--border-light)' }}>
                <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-1)' }}>Notifications</span>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{ fontSize: 11, color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>
                    Mark all read
                  </button>
                )}
              </div>
              {notifications.length === 0 ? (
                <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>No notifications</div>
              ) : (
                notifications.map(n => (
                  <div
                    key={n._id}
                    onClick={() => markRead(n)}
                    style={{
                      padding: '10px 12px', borderBottom: '1px solid var(--border-light)',
                      cursor: n.link ? 'pointer' : 'default',
                      background: n.read ? 'transparent' : 'var(--accent-dim)',
                      transition: 'background 0.1s'
                    }}
                  >
                    <div style={{ fontWeight: n.read ? 400 : 600, fontSize: 13, marginBottom: 2, color: 'var(--text-1)' }}>{n.title}</div>
                    {n.message && <div style={{ fontSize: 12, color: 'var(--text-2)', marginBottom: 3 }}>{n.message}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text-3)' }}>
                      {n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true }) : ''}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '10px 8px', overflowY: 'auto' }}>
          {items.map((item, i) => {
            if (item.divider) return (
              <div key={i} style={{ height: 1, background: 'var(--border-light)', margin: '8px 4px' }} />
            );
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                style={({ isActive }) => ({
                  display: 'flex', alignItems: 'center',
                  padding: '7px 10px', borderRadius: 'var(--r-sm)', marginBottom: 1,
                  color: isActive ? 'var(--accent)' : 'var(--text-2)',
                  background: isActive ? 'var(--accent-dim)' : 'transparent',
                  textDecoration: 'none', fontSize: 13.5, fontWeight: isActive ? 600 : 400,
                  transition: 'all 0.1s',
                })}
              >
                {item.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Bottom */}
        <div style={{ padding: '10px 8px', borderTop: '1px solid var(--border-light)' }}>
          {/* Status selector */}
          <div ref={statusRef} style={{ position: 'relative', marginBottom: 8 }}>
            <button
              onClick={() => setShowStatus(!showStatus)}
              style={{
                width: '100%', background: 'var(--surface-2)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-sm)', padding: '6px 10px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 7, fontSize: 13,
                color: 'var(--text-2)', fontFamily: 'inherit'
              }}
            >
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: sc.dot, flexShrink: 0 }} />
              <span style={{ flex: 1, textAlign: 'left' }}>{sc.label}</span>
              <span style={{ fontSize: 10, color: 'var(--text-3)' }}>▾</span>
            </button>
            {showStatus && (
              <div style={{
                position: 'absolute', bottom: '110%', left: 0, right: 0,
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--r-lg)', boxShadow: 'var(--shadow-md)',
                overflow: 'hidden', zIndex: 200
              }}>
                {Object.entries(STATUS).map(([key, cfg]) => (
                  <button
                    key={key}
                    onClick={() => handleStatus(key)}
                    style={{
                      width: '100%', padding: '8px 12px',
                      background: user?.status === key ? 'var(--surface-2)' : 'none',
                      border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
                      gap: 8, fontSize: 13, color: 'var(--text-1)', textAlign: 'left', fontFamily: 'inherit'
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: cfg.dot }} />
                    {cfg.label}
                    {user?.status === key && <span style={{ marginLeft: 'auto', color: 'var(--accent)', fontSize: 12 }}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* User row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px' }}>
            <div className="avatar" style={{ width: 28, height: 28, background: 'var(--accent-dim)', color: 'var(--accent)', fontSize: 11, fontWeight: 700 }}>
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', textTransform: 'capitalize' }}>{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--text-3)', fontSize: 13, padding: '3px 5px',
                borderRadius: 'var(--r-xs)', fontFamily: 'inherit'
              }}
            >↩</button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ marginLeft: 220, flex: 1, padding: '28px 32px', minHeight: '100vh' }}>
        <Outlet />
      </main>
    </div>
  );
}
